/*
 * @Descripttion: 上传发票弹框
 * @Author: 曹博
 * @Date: 2023-09-28 10:50:36
 * @LastEditors: 曹博
 * @LastEditTime: 2023-11-15 17:08:52
 */
import {
  Button,
  DatePicker,
  Form,
  FormInstance,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  message,
} from 'antd'
import { createContext, useEffect, useState } from 'react'
import Styles from './styles.module.scss'
import {
  DeleteFilled,
  PlusCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import UploadSingleInvoice from './UploadSingleInvoice'
import moment from 'moment'
import { CustomModalProps } from '../../../../components/ModalUser'
import { POST_RECOGNIZE_INVOICE } from '../../../../api/orderManage'
import { fileFile } from '../../../../modules/uploadfiles/hooks'
import {
  formatStr,
  getPicName,
  isResponseCodeSuccess,
} from '../../../../tools/util'
import { uniq } from 'lodash'
import { InvoiceNatureValue, invoiceTypeEnum } from '@/global/enum/invoice'
import uploadContext from '@/modules/uploadfiles/uploadContext'

export type FinanicialInvoiceType = 'batchUpload' | 'manual'

export interface Props extends CustomModalProps {
  form: FormInstance
  type: FinanicialInvoiceType
  loading?: boolean
  okBtnLoading?: boolean
  modalCancel: () => void
  modalSubmit: () => void
  invoiceNature?: InvoiceNatureValue
}

// 上传发票Form样式
export const invoiceFormItemLayout = {
  style: {
    width: '30%',
    marginRight: '8px',
  },
}

export const MyContext = createContext(false)
const InvoiceEntryBatchModal = (props: Props) => {
  const {
    invoiceNature = 'BLUE',
    form,
    type,
    loading,
    open,
    modalCancel,
    modalSubmit,
    ...restProps
  } = props
  const [disabledList, setDisabledList] = useState<number[]>([])
  const [requireFileIndex, setRequireFileIndex] = useState<number[]>([])
  const [checkCodeIndex, setCheckCodeIndex] = useState<number[]>([])
  const [requireInvoiceCodeIndex, setRequireInvoiceCodeIndex] = useState<
    number[]
  >([])
  const [modalLoading, setModalLoading] = useState(false)

  useEffect(() => {
    setDisabledList([])
    setRequireFileIndex([])
    setCheckCodeIndex([])
    if (open) {
      form.setFieldsValue({
        invoiceList: type === 'manual' ? [{}] : [],
      })
    }
    // eslint-disable-next-line
  }, [type, open])

  useEffect(() => {
    if (type === 'batchUpload' && !loading) {
      const {
        disabledList,
        requireList,
        checkCodeList,
        requireInvoiceCodeList,
      } = filterDisAndReqList(form.getFieldsValue().invoiceList || [])
      // 批量上传的发票存在发票附件，页面数据禁止修改
      setDisabledList(disabledList)
      // 根据批量上传的发票判断附件是否必填，避免删除附件后提交表单校验失效问题。
      setRequireFileIndex(requireList || [])
      // 判断是否展示校验码项
      setCheckCodeIndex(checkCodeList)
      // 判断发票代码必填
      setRequireInvoiceCodeIndex(requireInvoiceCodeList)
    }
    // eslint-disable-next-line
  }, [type, loading])

  const handleRecognize = async (key: number, file?: fileFile[]) => {
    if (file?.length) {
      message.loading('发票识别中，请耐心等待......')
      const fileUrls = file.map((item) => item.url).join(',')
      const res = await POST_RECOGNIZE_INVOICE({ fileUrls })
      const formData: any[] = form.getFieldValue('invoiceList')
      if (isResponseCodeSuccess(res.code)) {
        const resData = res.data
        if (
          resData.every(
            (item) =>
              item.invoiceNum &&
              item.invoiceTime &&
              item.totalTaxAmount &&
              item.totalAmountIncludingTax &&
              item.totalAmount &&
              item.invoiceType,
          )
        ) {
          const fileList = [
            {
              url: res.data[0].url,
              baseUrl: res.data[0].baseUrl,
              name: getPicName(res.data[0].url),
              id: res.data[0].fileId,
            },
          ]
          const newList = formData.map((item, index) => {
            if (index === key) {
              return {
                ...item,
                ...resData[0],
                fileList: fileList,
                invoiceType: resData[0].invoiceType.key,
                invoiceTime: moment(Number(resData[0].invoiceTime)),
                verificationType: '9',
                checkCode: invoiceTypeEnum.isLabelsMatchValue(
                  ['增值税普通发票', '增值税电子普通发票'],
                  resData?.[0]?.invoiceType?.key,
                )
                  ? resData[0].checkCode.slice(-6)
                  : '',
              }
            }
            return item
          })
          const {
            disabledList,
            requireList,
            checkCodeList,
            requireInvoiceCodeList,
          } = filterDisAndReqList(newList)
          setDisabledList(disabledList)
          setRequireFileIndex(requireList)
          setCheckCodeIndex(checkCodeList)
          setRequireInvoiceCodeIndex(requireInvoiceCodeList)
          form.setFieldsValue({
            invoiceList: newList,
          })
          setModalLoading(false)
          message.success('识别成功！')
        } else {
          form.setFieldsValue({
            invoiceList: formData.map((item, index) => {
              if (index === key) {
                return {
                  ...item,
                  fileList: [],
                }
              }
              return item
            }),
          })
          setModalLoading(false)
          message.error('识别失败！')
        }
      }
    } else {
      setModalLoading(false)
      setDisabledList(disabledList.filter((item) => item !== key))
    }
  }

  // 修改发票种类
  const changeInvoiceType = (type: string, index: number) => {
    // 判断附件是否必传
    if (
      invoiceTypeEnum.isLabelsMatchValue(
        [
          '增值税电子专用票',
          '电子发票（增值税专用发票）',
          '增值税电子普通发票',
          '电子发票（普通发票）',
        ],
        type,
      )
    ) {
      setRequireFileIndex(uniq([...requireFileIndex, index]))
    } else {
      setRequireFileIndex(requireFileIndex.filter((item) => item !== index))
    }
    // 当发票类型为增值税普通发票时，展示校验码输入框
    if (
      invoiceTypeEnum.isLabelsMatchValue(
        ['增值税普通发票', '增值税电子普通发票'],
        type,
      )
    ) {
      setCheckCodeIndex(uniq([...checkCodeIndex, index]))
    } else {
      setCheckCodeIndex(checkCodeIndex.filter((item) => item !== index))
    }
    // 发票种类不是电子发票（增值税专用发票）时，发票代码必填
    if (
      !invoiceTypeEnum.isLabelsMatchValue(
        ['电子发票（增值税专用发票）', '电子发票（普通发票）'],
        type,
      )
    ) {
      setRequireInvoiceCodeIndex(uniq([...requireInvoiceCodeIndex, index]))
    } else {
      setRequireInvoiceCodeIndex(
        requireInvoiceCodeIndex.filter((item) => item !== index),
      )
    }
  }

  const delInvoice = (name: number) => {
    const newList = form
      .getFieldsValue()
      .invoiceList.filter((item: any, index: number) => index !== name)
    form.setFieldsValue({ invoiceList: newList })
    const { disabledList, requireList, checkCodeList, requireInvoiceCodeList } =
      filterDisAndReqList(newList)
    setDisabledList(disabledList)
    setRequireFileIndex(requireList)
    setCheckCodeIndex(checkCodeList)
    setRequireInvoiceCodeIndex(requireInvoiceCodeList)
  }

  // 过滤发票列表，判断那些需要禁用，那些发票附件必传，那些发票需要填写校验码
  const filterDisAndReqList = (arr: any) => {
    const disabledList = arr
      .map((item: any, index: number) => {
        if (item.fileList && item.fileList.length) {
          return index
        }
      })
      .filter((item: any) => item === 0 || item)
    const requireList = arr
      .map((item: any, index: number) => {
        if (
          invoiceTypeEnum.isLabelsMatchValue(
            [
              '增值税电子专用票',
              '电子发票（增值税专用发票）',
              '增值税电子普通发票',
              '电子发票（普通发票）',
            ],
            item.invoiceType,
          )
        ) {
          return index
        }
      })
      .filter((item: number) => item === 0 || item)
    // 过滤那些发票展示校验码项
    const checkCodeList = arr
      .map((item: any, index: number) => {
        if (
          invoiceTypeEnum.isLabelsMatchValue(
            ['增值税普通发票', '增值税电子普通发票'],
            item.invoiceType,
          )
        ) {
          return index
        }
      })
      .filter((item: number) => item === 0 || item)
    // 过滤那些发票的发票代码必填
    const requireInvoiceCodeList = arr
      .map((item: any, index: number) => {
        if (
          !invoiceTypeEnum.isLabelsMatchValue(
            ['电子发票（增值税专用发票）', '电子发票（普通发票）'],
            item.invoiceType,
          )
        ) {
          return index
        }
      })
      .filter((item: number) => item === 0 || item)
    return { disabledList, requireList, checkCodeList, requireInvoiceCodeList }
  }

  return (
    <Modal
      title="上传发票"
      width={1000}
      bodyStyle={{ height: '500px', overflowY: 'auto' }}
      {...restProps}
      className={Styles['modal']}
      open={open}
      onCancel={modalCancel}
      footer={[
        <Button key="back" onClick={modalCancel}>
          取消
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={modalLoading}
          onClick={modalSubmit}
        >
          提交
        </Button>,
      ]}
    >
      <Spin
        spinning={type === 'batchUpload' && loading}
        indicator={
          <div>
            <Space style={{ color: 'orange' }}>
              <LoadingOutlined style={{ fontSize: 24 }} spin />
              发票识别中......请耐心等待
            </Space>
          </div>
        }
        wrapperClassName={Styles['spin-box']}
      >
        <Form
          form={form}
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          preserve={false}
        >
          <Form.List name="invoiceList">
            {(fields, { add }) => {
              return (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <div
                      key={key}
                      className={`${Styles['invoice-item']} ${Styles['marginBottom-20']}`}
                    >
                      <div className={Styles['item-left']}>
                        <span>{`发票${index + 1}`}</span>
                        <span>
                          <Button
                            hidden={index === 0}
                            type="link"
                            danger
                            onClick={() => {
                              delInvoice(name)
                            }}
                            icon={<DeleteFilled />}
                          />
                        </span>
                      </div>
                      <div className={Styles['item-right']}>
                        <Row>
                          <uploadContext.Provider
                            value={{ modalLoading, setModalLoading }}
                          >
                            <Form.Item
                              {...invoiceFormItemLayout}
                              {...restField}
                              name={[name, 'fileList']}
                              label="发票附件"
                              rules={[
                                {
                                  required: requireFileIndex.includes(index),
                                  message: '请上传电子发票的发票附件',
                                },
                              ]}
                            >
                              <UploadSingleInvoice
                                hiddenDownload
                                onChange={(file) => handleRecognize(name, file)}
                              />
                            </Form.Item>
                          </uploadContext.Provider>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            label="发票种类"
                            name={[name, 'invoiceType']}
                            rules={[{ message: '请选择', required: true }]}
                          >
                            <Select
                              placeholder="请选择"
                              disabled={disabledList.includes(index)}
                              onSelect={(value: string) => {
                                changeInvoiceType(value, index)
                              }}
                              options={invoiceTypeEnum}
                            ></Select>
                          </Form.Item>
                        </Row>
                        <Row>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            {...restField}
                            name={[name, 'invoiceNum']}
                            label={'发票号码'}
                            rules={[
                              {
                                required: true,
                                message: '请输入',
                              },
                            ]}
                            getValueFromEvent={(e) => {
                              return formatStr(e.target.value, 'Number')
                            }}
                          >
                            <Input
                              placeholder="请输入"
                              disabled={disabledList.includes(index)}
                            ></Input>
                          </Form.Item>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            {...restField}
                            name={[name, 'invoiceCode']}
                            label={'发票代码'}
                            getValueFromEvent={(e) => {
                              return formatStr(e.target.value, 'Number')
                            }}
                            rules={[
                              {
                                required:
                                  requireInvoiceCodeIndex.includes(index),
                                message: '请输入发票代码',
                              },
                            ]}
                          >
                            <Input
                              placeholder="请输入"
                              disabled={disabledList.includes(index)}
                            ></Input>
                          </Form.Item>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            {...restField}
                            name={[name, 'invoiceTime']}
                            label={'开票日期'}
                            rules={[
                              {
                                required: true,
                                message: '请选择日期',
                              },
                            ]}
                          >
                            <DatePicker
                              disabled={disabledList.includes(index)}
                            />
                          </Form.Item>
                        </Row>
                        <Row>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            {...restField}
                            name={[name, 'totalAmount']}
                            label={'合计金额'}
                            rules={[
                              invoiceNature === 'BLUE'
                                ? {
                                    required: true,
                                    pattern:
                                      /^(0\.\d{1,2}|[1-9]\d*(\.\d{1,2})?)$/,
                                    message: '仅支持大于零的两位小数',
                                  }
                                : {
                                    required: true,
                                    pattern: /^-([1-9]\d*(\.\d{1,2})?|0\.\d{1,2})$/,
                                    message: '仅支持小于零的两位小数',
                                  },
                            ]}
                          >
                            <Input
                              placeholder="请输入"
                              disabled={disabledList.includes(index)}
                            ></Input>
                          </Form.Item>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            {...restField}
                            name={[name, 'totalTaxAmount']}
                            label={'合计税额'}
                            rules={[
                              invoiceNature === 'BLUE'
                                ? {
                                    pattern: /^(0\.\d{1,2}|[1-9]\d*(\.\d{1,2})?)$/,
                                    message: '仅支持大于零的两位小数',
                                  }
                                : {
                                    pattern: /^-([1-9]\d*(\.\d{1,2})?|0\.\d{1,2})$/,
                                    message: '仅支持小于零的两位小数',
                                  },
                            ]}
                          >
                            <Input
                              placeholder="请输入"
                              disabled={disabledList.includes(index)}
                            ></Input>
                          </Form.Item>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            {...restField}
                            name={[name, 'totalAmountIncludingTax']}
                            label={'价税合计'}
                            rules={[
                              invoiceNature === 'BLUE'
                                ? {
                                    required: true,
                                    pattern: /^(0\.\d{1,2}|[1-9]\d*(\.\d{1,2})?)$/,
                                    message: '仅支持大于零的两位小数',
                                  }
                                : {
                                    required: true,
                                    pattern: /^-([1-9]\d*(\.\d{1,2})?|0\.\d{1,2})$/,
                                    message: '仅支持小于零的两位小数',
                                  },
                            ]}
                          >
                            <Input
                              placeholder="请输入"
                              disabled={disabledList.includes(index)}
                            ></Input>
                          </Form.Item>
                        </Row>
                        {checkCodeIndex.includes(index) && (
                          <Row>
                            <Form.Item
                              {...invoiceFormItemLayout}
                              label={'校验码'}
                              {...restField}
                              name={[name, 'checkCode']}
                              rules={[
                                {
                                  required: true,
                                  message: '请输入校验码',
                                },
                              ]}
                              getValueFromEvent={(e) => {
                                return formatStr(e.target.value, 'Number')
                              }}
                            >
                              <Input
                                placeholder="请输入"
                                disabled={disabledList.includes(index)}
                                maxLength={6}
                              ></Input>
                            </Form.Item>
                            <span
                              style={{
                                marginTop: '2px',
                                fontSize: '12px',
                                color: '#CCCCCC',
                              }}
                            >
                              填写后六位
                            </span>
                          </Row>
                        )}
                      </div>
                    </div>
                  ))}
                  {!loading && fields.length < 10 && (
                    <div className={Styles['invoice-item']}>
                      <div className={Styles['item-left']}></div>
                      <div className={Styles['item-right']}>
                        <Button
                          size="middle"
                          onClick={() => add()}
                          icon={<PlusCircleOutlined />}
                        >
                          添加发票
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )
            }}
          </Form.List>
        </Form>
      </Spin>
    </Modal>
  )
}
export default InvoiceEntryBatchModal
/*
 * @Descripttion: 上传发票弹框
 * @Author: 曹博
 * @Date: 2023-09-28 10:50:36
 * @LastEditors: 曹博
 * @LastEditTime: 2023-11-15 17:08:52
 */
import {
  Button,
  DatePicker,
  Form,
  FormInstance,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  message,
} from 'antd'
import { createContext, useEffect, useState } from 'react'
import Styles from './styles.module.scss'
import {
  DeleteFilled,
  PlusCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import UploadSingleInvoice from './UploadSingleInvoice'
import moment from 'moment'
import { CustomModalProps } from '../../../../components/ModalUser'
import { POST_RECOGNIZE_INVOICE } from '../../../../api/orderManage'
import { fileFile } from '../../../../modules/uploadfiles/hooks'
import {
  formatStr,
  getPicName,
  isResponseCodeSuccess,
} from '../../../../tools/util'
import { uniq } from 'lodash'
import { InvoiceNatureValue, invoiceTypeEnum } from '@/global/enum/invoice'
import uploadContext from '@/modules/uploadfiles/uploadContext'

export type FinanicialInvoiceType = 'batchUpload' | 'manual'

export interface Props extends CustomModalProps {
  form: FormInstance
  type: FinanicialInvoiceType
  loading?: boolean
  okBtnLoading?: boolean
  modalCancel: () => void
  modalSubmit: () => void
  invoiceNature?: InvoiceNatureValue
}

// 上传发票Form样式
export const invoiceFormItemLayout = {
  style: {
    width: '30%',
    marginRight: '8px',
  },
}

export const MyContext = createContext(false)
const InvoiceEntryBatchModal = (props: Props) => {
  const {
    invoiceNature = 'BLUE',
    form,
    type,
    loading,
    open,
    modalCancel,
    modalSubmit,
    ...restProps
  } = props
  const [disabledList, setDisabledList] = useState<number[]>([])
  const [requireFileIndex, setRequireFileIndex] = useState<number[]>([])
  const [checkCodeIndex, setCheckCodeIndex] = useState<number[]>([])
  const [requireInvoiceCodeIndex, setRequireInvoiceCodeIndex] = useState<
    number[]
  >([])
  const [modalLoading, setModalLoading] = useState(false)

  useEffect(() => {
    setDisabledList([])
    setRequireFileIndex([])
    setCheckCodeIndex([])
    if (open) {
      form.setFieldsValue({
        invoiceList: type === 'manual' ? [{}] : [],
      })
    }
    // eslint-disable-next-line
  }, [type, open])

  useEffect(() => {
    if (type === 'batchUpload' && !loading) {
      const {
        disabledList,
        requireList,
        checkCodeList,
        requireInvoiceCodeList,
      } = filterDisAndReqList(form.getFieldsValue().invoiceList || [])
      // 批量上传的发票存在发票附件，页面数据禁止修改
      setDisabledList(disabledList)
      // 根据批量上传的发票判断附件是否必填，避免删除附件后提交表单校验失效问题。
      setRequireFileIndex(requireList || [])
      // 判断是否展示校验码项
      setCheckCodeIndex(checkCodeList)
      // 判断发票代码必填
      setRequireInvoiceCodeIndex(requireInvoiceCodeList)
    }
    // eslint-disable-next-line
  }, [type, loading])

  const handleRecognize = async (key: number, file?: fileFile[]) => {
    if (file?.length) {
      message.loading('发票识别中，请耐心等待......')
      const fileUrls = file.map((item) => item.url).join(',')
      const res = await POST_RECOGNIZE_INVOICE({ fileUrls })
      const formData: any[] = form.getFieldValue('invoiceList')
      if (isResponseCodeSuccess(res.code)) {
        const resData = res.data
        if (
          resData.every(
            (item) =>
              item.invoiceNum &&
              item.invoiceTime &&
              item.totalTaxAmount &&
              item.totalAmountIncludingTax &&
              item.totalAmount &&
              item.invoiceType,
          )
        ) {
          const fileList = [
            {
              url: res.data[0].url,
              baseUrl: res.data[0].baseUrl,
              name: getPicName(res.data[0].url),
              id: res.data[0].fileId,
            },
          ]
          const newList = formData.map((item, index) => {
            if (index === key) {
              return {
                ...item,
                ...resData[0],
                fileList: fileList,
                invoiceType: resData[0].invoiceType.key,
                invoiceTime: moment(Number(resData[0].invoiceTime)),
                verificationType: '9',
                checkCode: invoiceTypeEnum.isLabelsMatchValue(
                  ['增值税普通发票', '增值税电子普通发票'],
                  resData?.[0]?.invoiceType?.key,
                )
                  ? resData[0].checkCode.slice(-6)
                  : '',
              }
            }
            return item
          })
          const {
            disabledList,
            requireList,
            checkCodeList,
            requireInvoiceCodeList,
          } = filterDisAndReqList(newList)
          setDisabledList(disabledList)
          setRequireFileIndex(requireList)
          setCheckCodeIndex(checkCodeList)
          setRequireInvoiceCodeIndex(requireInvoiceCodeList)
          form.setFieldsValue({
            invoiceList: newList,
          })
          setModalLoading(false)
          message.success('识别成功！')
        } else {
          form.setFieldsValue({
            invoiceList: formData.map((item, index) => {
              if (index === key) {
                return {
                  ...item,
                  fileList: [],
                }
              }
              return item
            }),
          })
          setModalLoading(false)
          message.error('识别失败！')
        }
      }
    } else {
      setModalLoading(false)
      setDisabledList(disabledList.filter((item) => item !== key))
    }
  }

  // 修改发票种类
  const changeInvoiceType = (type: string, index: number) => {
    // 判断附件是否必传
    if (
      invoiceTypeEnum.isLabelsMatchValue(
        [
          '增值税电子专用票',
          '电子发票（增值税专用发票）',
          '增值税电子普通发票',
          '电子发票（普通发票）',
        ],
        type,
      )
    ) {
      setRequireFileIndex(uniq([...requireFileIndex, index]))
    } else {
      setRequireFileIndex(requireFileIndex.filter((item) => item !== index))
    }
    // 当发票类型为增值税普通发票时，展示校验码输入框
    if (
      invoiceTypeEnum.isLabelsMatchValue(
        ['增值税普通发票', '增值税电子普通发票'],
        type,
      )
    ) {
      setCheckCodeIndex(uniq([...checkCodeIndex, index]))
    } else {
      setCheckCodeIndex(checkCodeIndex.filter((item) => item !== index))
    }
    // 发票种类不是电子发票（增值税专用发票）时，发票代码必填
    if (
      !invoiceTypeEnum.isLabelsMatchValue(
        ['电子发票（增值税专用发票）', '电子发票（普通发票）'],
        type,
      )
    ) {
      setRequireInvoiceCodeIndex(uniq([...requireInvoiceCodeIndex, index]))
    } else {
      setRequireInvoiceCodeIndex(
        requireInvoiceCodeIndex.filter((item) => item !== index),
      )
    }
  }

  const delInvoice = (name: number) => {
    const newList = form
      .getFieldsValue()
      .invoiceList.filter((item: any, index: number) => index !== name)
    form.setFieldsValue({ invoiceList: newList })
    const { disabledList, requireList, checkCodeList, requireInvoiceCodeList } =
      filterDisAndReqList(newList)
    setDisabledList(disabledList)
    setRequireFileIndex(requireList)
    setCheckCodeIndex(checkCodeList)
    setRequireInvoiceCodeIndex(requireInvoiceCodeList)
  }

  // 过滤发票列表，判断那些需要禁用，那些发票附件必传，那些发票需要填写校验码
  const filterDisAndReqList = (arr: any) => {
    const disabledList = arr
      .map((item: any, index: number) => {
        if (item.fileList && item.fileList.length) {
          return index
        }
      })
      .filter((item: any) => item === 0 || item)
    const requireList = arr
      .map((item: any, index: number) => {
        if (
          invoiceTypeEnum.isLabelsMatchValue(
            [
              '增值税电子专用票',
              '电子发票（增值税专用发票）',
              '增值税电子普通发票',
              '电子发票（普通发票）',
            ],
            item.invoiceType,
          )
        ) {
          return index
        }
      })
      .filter((item: number) => item === 0 || item)
    // 过滤那些发票展示校验码项
    const checkCodeList = arr
      .map((item: any, index: number) => {
        if (
          invoiceTypeEnum.isLabelsMatchValue(
            ['增值税普通发票', '增值税电子普通发票'],
            item.invoiceType,
          )
        ) {
          return index
        }
      })
      .filter((item: number) => item === 0 || item)
    // 过滤那些发票的发票代码必填
    const requireInvoiceCodeList = arr
      .map((item: any, index: number) => {
        if (
          !invoiceTypeEnum.isLabelsMatchValue(
            ['电子发票（增值税专用发票）', '电子发票（普通发票）'],
            item.invoiceType,
          )
        ) {
          return index
        }
      })
      .filter((item: number) => item === 0 || item)
    return { disabledList, requireList, checkCodeList, requireInvoiceCodeList }
  }

  return (
    <Modal
      title="上传发票"
      width={1000}
      bodyStyle={{ height: '500px', overflowY: 'auto' }}
      {...restProps}
      className={Styles['modal']}
      open={open}
      onCancel={modalCancel}
      footer={[
        <Button key="back" onClick={modalCancel}>
          取消
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={modalLoading}
          onClick={modalSubmit}
        >
          提交
        </Button>,
      ]}
    >
      <Spin
        spinning={type === 'batchUpload' && loading}
        indicator={
          <div>
            <Space style={{ color: 'orange' }}>
              <LoadingOutlined style={{ fontSize: 24 }} spin />
              发票识别中......请耐心等待
            </Space>
          </div>
        }
        wrapperClassName={Styles['spin-box']}
      >
        <Form
          form={form}
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          preserve={false}
        >
          <Form.List name="invoiceList">
            {(fields, { add }) => {
              return (
                <>
                  {fields.map(({ key, name, ...restField }, index) => (
                    <div
                      key={key}
                      className={`${Styles['invoice-item']} ${Styles['marginBottom-20']}`}
                    >
                      <div className={Styles['item-left']}>
                        <span>{`发票${index + 1}`}</span>
                        <span>
                          <Button
                            hidden={index === 0}
                            type="link"
                            danger
                            onClick={() => {
                              delInvoice(name)
                            }}
                            icon={<DeleteFilled />}
                          />
                        </span>
                      </div>
                      <div className={Styles['item-right']}>
                        <Row>
                          <uploadContext.Provider
                            value={{ modalLoading, setModalLoading }}
                          >
                            <Form.Item
                              {...invoiceFormItemLayout}
                              {...restField}
                              name={[name, 'fileList']}
                              label="发票附件"
                              rules={[
                                {
                                  required: requireFileIndex.includes(index),
                                  message: '请上传电子发票的发票附件',
                                },
                              ]}
                            >
                              <UploadSingleInvoice
                                hiddenDownload
                                onChange={(file) => handleRecognize(name, file)}
                              />
                            </Form.Item>
                          </uploadContext.Provider>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            label="发票种类"
                            name={[name, 'invoiceType']}
                            rules={[{ message: '请选择', required: true }]}
                          >
                            <Select
                              placeholder="请选择"
                              disabled={disabledList.includes(index)}
                              onSelect={(value: string) => {
                                changeInvoiceType(value, index)
                              }}
                              options={invoiceTypeEnum}
                            ></Select>
                          </Form.Item>
                        </Row>
                        <Row>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            {...restField}
                            name={[name, 'invoiceNum']}
                            label={'发票号码'}
                            rules={[
                              {
                                required: true,
                                message: '请输入',
                              },
                            ]}
                            getValueFromEvent={(e) => {
                              return formatStr(e.target.value, 'Number')
                            }}
                          >
                            <Input
                              placeholder="请输入"
                              disabled={disabledList.includes(index)}
                            ></Input>
                          </Form.Item>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            {...restField}
                            name={[name, 'invoiceCode']}
                            label={'发票代码'}
                            getValueFromEvent={(e) => {
                              return formatStr(e.target.value, 'Number')
                            }}
                            rules={[
                              {
                                required:
                                  requireInvoiceCodeIndex.includes(index),
                                message: '请输入发票代码',
                              },
                            ]}
                          >
                            <Input
                              placeholder="请输入"
                              disabled={disabledList.includes(index)}
                            ></Input>
                          </Form.Item>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            {...restField}
                            name={[name, 'invoiceTime']}
                            label={'开票日期'}
                            rules={[
                              {
                                required: true,
                                message: '请选择日期',
                              },
                            ]}
                          >
                            <DatePicker
                              disabled={disabledList.includes(index)}
                            />
                          </Form.Item>
                        </Row>
                        <Row>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            {...restField}
                            name={[name, 'totalAmount']}
                            label={'合计金额'}
                            rules={[
                              invoiceNature === 'BLUE'
                                ? {
                                    required: true,
                                    pattern:
                                      /^(0\.\d{1,2}|[1-9]\d*(\.\d{1,2})?)$/,
                                    message: '仅支持大于零的两位小数',
                                  }
                                : {
                                    required: true,
                                    pattern: /^-([1-9]\d*(\.\d{1,2})?|0\.\d{1,2})$/,
                                    message: '仅支持小于零的两位小数',
                                  },
                            ]}
                          >
                            <Input
                              placeholder="请输入"
                              disabled={disabledList.includes(index)}
                            ></Input>
                          </Form.Item>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            {...restField}
                            name={[name, 'totalTaxAmount']}
                            label={'合计税额'}
                            rules={[
                              invoiceNature === 'BLUE'
                                ? {
                                    pattern: /^(0\.\d{1,2}|[1-9]\d*(\.\d{1,2})?)$/,
                                    message: '仅支持大于零的两位小数',
                                  }
                                : {
                                    pattern: /^-([1-9]\d*(\.\d{1,2})?|0\.\d{1,2})$/,
                                    message: '仅支持小于零的两位小数',
                                  },
                            ]}
                          >
                            <Input
                              placeholder="请输入"
                              disabled={disabledList.includes(index)}
                            ></Input>
                          </Form.Item>
                          <Form.Item
                            {...invoiceFormItemLayout}
                            {...restField}
                            name={[name, 'totalAmountIncludingTax']}
                            label={'价税合计'}
                            rules={[
                              invoiceNature === 'BLUE'
                                ? {
                                    required: true,
                                    pattern: /^(0\.\d{1,2}|[1-9]\d*(\.\d{1,2})?)$/,
                                    message: '仅支持大于零的两位小数',
                                  }
                                : {
                                    required: true,
                                    pattern: /^-([1-9]\d*(\.\d{1,2})?|0\.\d{1,2})$/,
                                    message: '仅支持小于零的两位小数',
                                  },
                            ]}
                          >
                            <Input
                              placeholder="请输入"
                              disabled={disabledList.includes(index)}
                            ></Input>
                          </Form.Item>
                        </Row>
                        {checkCodeIndex.includes(index) && (
                          <Row>
                            <Form.Item
                              {...invoiceFormItemLayout}
                              label={'校验码'}
                              {...restField}
                              name={[name, 'checkCode']}
                              rules={[
                                {
                                  required: true,
                                  message: '请输入校验码',
                                },
                              ]}
                              getValueFromEvent={(e) => {
                                return formatStr(e.target.value, 'Number')
                              }}
                            >
                              <Input
                                placeholder="请输入"
                                disabled={disabledList.includes(index)}
                                maxLength={6}
                              ></Input>
                            </Form.Item>
                            <span
                              style={{
                                marginTop: '2px',
                                fontSize: '12px',
                                color: '#CCCCCC',
                              }}
                            >
                              填写后六位
                            </span>
                          </Row>
                        )}
                      </div>
                    </div>
                  ))}
                  {!loading && fields.length < 10 && (
                    <div className={Styles['invoice-item']}>
                      <div className={Styles['item-left']}></div>
                      <div className={Styles['item-right']}>
                        <Button
                          size="middle"
                          onClick={() => add()}
                          icon={<PlusCircleOutlined />}
                        >
                          添加发票
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )
            }}
          </Form.List>
        </Form>
      </Spin>
    </Modal>
  )
}
export default InvoiceEntryBatchModal
